import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { Keyboard } from 'lucide-react';
import { ProductLine } from 'types';
import { SCREEN_TYPES } from 'utils/constants';
import { useAppStore, useProductStore, useReceiptStore } from 'store';
import { toast } from 'sonner';
import { Button } from 'components/ui/Button';
import { CardTitle } from 'components/ui/card';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { useKeyboardInputToggle } from 'hooks/useKeyboardInputToggle';
import { getBarcodeAction, isInputBarcode } from 'services/barcodes';
import { BarcodeType } from 'types';
import { handleProblemResolve } from 'utils/handleProblem';

export default function ScanItem() {
  const [productId, setProductId] = useState('');
  const [loading, setLoading] = useState(false);
  const productInputRef = useRef<HTMLInputElement>(null);
  const { keyboardVisible, toggleKeyboardInput } = useKeyboardInputToggle(productInputRef);
  const { setCurrentScreen, setBarcodeDebugMessage } = useAppStore();
  const { setCurrentProduct, currentProduct: productLine } = useProductStore();
  const { receiptData } = useReceiptStore();
  const [loadingProblemClicked, setLoadingProblemClicked] = useState(false);
  const { setProductNew, setProductCondition } = useProductStore();

  useEffect(() => {
    if (productLine) {
      setProductId(productLine.upc ? productLine.upc.toString() : '');
    }
  }, [productLine]);

  const onItemScanned = (product: ProductLine): void => {
    setCurrentProduct(product);
    setCurrentScreen(SCREEN_TYPES.CONFIRM_ITEM);
  };

  const handleSubmit = async (): Promise<void> => {
    const trimmedProductId = productId.trim();

    if (!trimmedProductId) {
      toast.warning('Please enter product number');
      return;
    }

    const barcodeType = isInputBarcode(trimmedProductId) as BarcodeType | null;
    if (barcodeType) {
      setBarcodeDebugMessage(`Barcode detected: ${barcodeType}`);
      getBarcodeAction(barcodeType, SCREEN_TYPES.SCAN_ITEM, {
        setProductNew,
        setProductCondition,
        setBarcodeDebugMessage,
      });
      setProductId('');
      return;
    }

    setLoading(true);

    const normalizedProductId = trimmedProductId.toLowerCase();
    const lines = (receiptData?.lines || receiptData?.receipt?.lines || []) as ProductLine[];

    const matchedLine = lines.find((line) => {
      if (!line) return false;
      const upcMatch = line.upc && line.upc.toString().trim().toLowerCase() === normalizedProductId;
      const productIdMatch =
        line.product_id && line.product_id.toString().trim().toLowerCase() === normalizedProductId;
      const isOpen = line.line_grade_status !== 'completed';
      return (upcMatch || productIdMatch) && isOpen;
    });

    if (matchedLine) {
      // setProductId('');
      onItemScanned(matchedLine);
    } else {
      // Case 1: Product not in receipt - create unknown product
      const unknownProduct: ProductLine = {
        transaction_id: receiptData?.transaction_id || receiptData?.receipt?.transaction_id,
        receipt_line: null,
        product_id: 'Unknown',
        style_color: 'N/A',
        product_size: 'N/A',
        expected_ind: 'n',
        upc: trimmedProductId,
        style_image_url: null,
        line_grade_status: 'open',
      };
      // setProductId('');
      onItemScanned(unknownProduct);
    }
  };

  const onCancel = () => setCurrentScreen(SCREEN_TYPES.SCAN_BOX);

  const onProblemClick = async () => {
    setLoadingProblemClicked(true);
    const result = await handleProblemResolve(SCREEN_TYPES.SCAN_ITEM);
    if (!result.success) {
      setLoading(false);
    }
    setLoadingProblemClicked(false);
  };

  return (
    <div className="space-y-4 sm:space-y-5">
      <CardTitle className="mb-1 flex items-center justify-center gap-3 text-center text-xl font-extrabold tracking-tight text-slate-900 sm:mb-2 sm:text-2xl">
        Scan Item
      </CardTitle>

      <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
        <span className="mr-2 text-sm font-semibold text-slate-600 sm:min-w-[100px] sm:shrink-0">
          Tracking #
        </span>
        <span className="break-all text-sm font-semibold text-slate-900">
          {receiptData?.tracking_id || receiptData?.receipt?.tracking_id || ''}
        </span>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm sm:p-4">
        <Label htmlFor="product-id" className="mb-2 block text-sm font-semibold text-slate-700">
          UPC
        </Label>
        <div className="flex items-center gap-2.5">
          <Input
            ref={productInputRef}
            id="product-id"
            type="text"
            inputMode={keyboardVisible ? 'text' : 'none'}
            placeholder="Input field"
            value={productId}
            onChange={(e) => setProductId(e.target.value)}
            onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => e.key === 'Enter' && handleSubmit()}
            disabled={loading}
            autoFocus
            className="text-base"
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-12 w-12 shrink-0 rounded-xl border-slate-300"
            onClick={toggleKeyboardInput}
            aria-pressed={keyboardVisible}
          >
            <Keyboard className="h-5 w-5" />
          </Button>
        </div>
      </div>

      <div className="buttons-container">
        <Button
          type="button"
          variant="secondary"
          className="h-12 flex-1 rounded-xl text-base font-semibold"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="destructive"
          className="h-12 flex-1 rounded-xl text-base font-semibold shadow-sm"
          disabled={loadingProblemClicked}
          onClick={onProblemClick}
        >
          Problem
        </Button>
      </div>
    </div>
  );
}
